import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSidebar from '@/components/AdminSidebar';
import ImageUpload from '@/components/ImageUpload';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    judul_welcome: '', subtitle_welcome: '', teks_welcome: '',
    tahun_ajaran: '', semester: 'Ganjil',
    whatsapp_pesantren: '', rekening_bank: '', nama_pesantren: '',
    logo_pesantren: '',
    nama_baris1: 'Pondok Pesantren',
    nama_baris2: "Baitul Qur'an",
    nama_baris3: 'Dabo Singkep',
    lokasi_pesantren: 'Kabupaten Lingga, Provinsi Kepulauan Riau.',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => setSettings(prev => ({...prev, ...data}))).catch(() => {});
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/settings`, settings);
      toast.success('Pengaturan berhasil disimpan');
    } catch { toast.error('Gagal menyimpan'); } finally { setLoading(false); }
  };

  const set = (key) => (e) => setSettings(s => ({...s, [key]: e.target.value}));

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content bg-gray-50 min-h-screen">
        <div className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-6">
          <h1 className="font-playfair text-2xl font-bold text-emerald-950">Pengaturan CMS</h1>
          <p className="text-sm text-gray-500">Kelola konten dan informasi pesantren</p>
        </div>
        <div className="p-4 md:p-8 max-w-2xl">
          <div className="space-y-6">
            {/* Halaman Sambutan */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-card border border-gray-50">
              <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-2 h-5 bg-emerald-500 rounded-full" /> Halaman Sambutan
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Judul Utama</label>
                  <input value={settings.judul_welcome} onChange={set('judul_welcome')}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sub-judul / Lokasi</label>
                  <input value={settings.subtitle_welcome} onChange={set('subtitle_welcome')}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Teks Sambutan</label>
                  <textarea value={settings.teks_welcome} onChange={set('teks_welcome')} rows={3}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none" />
                </div>
              </div>
            </motion.div>

            {/* Akademik */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-card border border-gray-50">
              <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-2 h-5 bg-blue-500 rounded-full" /> Status Akademik
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tahun Ajaran</label>
                  <input value={settings.tahun_ajaran} onChange={set('tahun_ajaran')} placeholder="2024/2025"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Semester</label>
                  <select value={settings.semester} onChange={set('semester')}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                    <option value="Ganjil">Ganjil</option>
                    <option value="Genap">Genap</option>
                  </select>
                </div>
              </div>
            </motion.div>

            {/* Kontak & Rekening */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-card border border-gray-50">
              <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-2 h-5 bg-gold-500 rounded-full" /> Kontak & Rekening
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Pesantren</label>
                  <input value={settings.nama_pesantren} onChange={set('nama_pesantren')}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">WhatsApp Pesantren (62xxx)</label>
                  <input value={settings.whatsapp_pesantren} onChange={set('whatsapp_pesantren')}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Rekening Bank Donasi</label>
                  <input value={settings.rekening_bank} onChange={set('rekening_bank')}
                    placeholder="BRI - 1234567890 a.n Pesantren..."
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
              </div>
            </motion.div>

            {/* Identitas Halaman Sambutan */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              className="bg-white rounded-2xl p-6 shadow-card border border-gray-50">
              <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-2 h-5 bg-emerald-500 rounded-full" /> Identitas Halaman Sambutan
              </h2>
              <p className="text-xs text-gray-400 mb-4">Tampil di halaman /welcome. Baris kedua nama pesantren akan berwarna hijau emerald.</p>
              <div className="space-y-4">
                <ImageUpload
                  label="Logo Pesantren"
                  value={settings.logo_pesantren}
                  onChange={v => setSettings(s => ({ ...s, logo_pesantren: v }))}
                />
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Pesantren — Baris 1 <span className="text-gray-400 font-normal">(warna hitam)</span></label>
                  <input value={settings.nama_baris1} onChange={set('nama_baris1')} placeholder="Pondok Pesantren"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Pesantren — Baris 2 <span className="text-emerald-600 font-semibold">(warna hijau)</span></label>
                  <input value={settings.nama_baris2} onChange={set('nama_baris2')} placeholder="Baitul Qur'an"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Pesantren — Baris 3 <span className="text-gray-400 font-normal">(warna hitam)</span></label>
                  <input value={settings.nama_baris3} onChange={set('nama_baris3')} placeholder="Dabo Singkep"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Lokasi Pesantren</label>
                  <input value={settings.lokasi_pesantren} onChange={set('lokasi_pesantren')} placeholder="Kabupaten Lingga, Provinsi Kepulauan Riau."
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
              </div>
            </motion.div>

            <button data-testid="save-settings-btn" onClick={handleSave} disabled={loading}
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl text-sm font-semibold shadow-emerald transition-all disabled:opacity-60">
              <Save size={16} />
              {loading ? 'Menyimpan...' : 'Simpan Pengaturan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
